import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";

import SpinnerComponent from "../Components/GenericComponents/SpinnerComponent";
import PrimaryButton from "../Components/GenericComponents/PrimaryButton";
import OnboardMeetingAnnonymousUser from "../Components/Meetings/OnboardMeetingAnnonymousUser";
import JoinMeetingByCodeForm from "../Components/Meetings/JoinMeetingByCodeForm";
import { useNotify } from "../Helpers/Notifications";
import { useAuthService } from "../Context/AuthContext";
import { useMeetingsService } from "../Context/MeetingContext";
import { useLinksquaredService } from "../Context/LinksquaredContext";
import { encodeMeetingURL } from "../Helpers/deepLinkHelper";
import LocalStorage from "../Helpers/LocalStorage";
import { meetingJoinErrorMessage } from "../Helpers/meetingJoinErrorHelper";

const MeetingJoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslate();
  const { notifyError } = useNotify();
  const { user, authHasCompleted, handleLoginAnonUser, getCurrentUserNoRedirect } = useAuthService();
  const { joinByToken, joinByTokenUnauthenticated } = useMeetingsService();
  const { setDeepLink } = useLinksquaredService();

  const token = searchParams.get("token");
  const hasStoredSession = !!LocalStorage.getAuthenticationToken();
  const isAuthResolving = hasStoredSession && !authHasCompleted;

  const [isLoading, setIsLoading] = useState(!!token);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAnonymousForm, setShowAnonymousForm] = useState(false);
  const joinRequestRef = useRef(false);

  const handleJoinError = (error) => {
    const message = meetingJoinErrorMessage(error, t);

    setErrorMessage(message);
    notifyError(message);
  };

  const goHome = () => {
    navigate("/", { replace: true });
  };

  const handleJoinSuccess = (meeting) => {
    if (!meeting) {
      setIsLoading(false);
      setErrorMessage(t("general.something_wrong"));
      return;
    }
    setDeepLink(
      encodeMeetingURL(meeting.id, meeting.title, meeting.description)
    );
    goHome();
  };

  const handleAnonymousJoin = (meetingId, name, callLanguage, language) => {
    setIsLoading(true);
    joinByTokenUnauthenticated(
      token,
      name,
      callLanguage,
      language,
      (response) => {
        handleLoginAnonUser(response.data.user);
        LocalStorage.setAuthenthicationToken(response.data.token);
        LocalStorage.setRefreshToken(response.data.refresh_token);
        LocalStorage.setSSOFlag(true);
        setShowAnonymousForm(false);
        handleJoinSuccess(response.data.meeting);
      },
      (error) => {
        setIsLoading(false);
        handleJoinError(error);
      }
    );
  };

  useEffect(() => {
    if (isAuthResolving) {
      getCurrentUserNoRedirect(() => {}, () => {});
    }
  }, []);

  useEffect(() => {
    if (!token || joinRequestRef.current || isAuthResolving) {
      return;
    }

    if (!user) {
      setIsLoading(false);
      setShowAnonymousForm(true);
      return;
    }

    joinRequestRef.current = true;
    joinByToken(
      token,
      (response) => {
        handleJoinSuccess(response?.data?.meeting);
      },
      (error) => {
        joinRequestRef.current = false;
        setIsLoading(false);
        handleJoinError(error);
      }
    );
  }, [token, user, authHasCompleted, hasStoredSession]);

  if (!token) {
    return (
      <FormPage>
        <JoinMeetingByCodeForm
          onClose={window.electron ? goHome : undefined}
          onJoined={goHome}
          isAuthResolving={isAuthResolving}
          initialCredentials={location.state}
          showDesktopOption
        />
      </FormPage>
    );
  }

  if (showAnonymousForm) {
    return (
      <SpinnerComponent isLoading={isLoading}>
        <OnboardMeetingAnnonymousUser
          meeting={{ id: token }}
          requestToJoin={handleAnonymousJoin}
          handleMeetingHasEnded={goHome}
        />
      </SpinnerComponent>
    );
  }

  return (
    <SpinnerComponent isLoading={isLoading}>
      <Page>
        <Card>
          {errorMessage ? (
            <>
              <Message>{errorMessage}</Message>
              <PrimaryButton onClick={goHome} style={{ padding: "12px 16px" }}>
                {t("general.ok")}
              </PrimaryButton>
            </>
          ) : (
            <Message>{t("general.loading")}</Message>
          )}
        </Card>
      </Page>
    </SpinnerComponent>
  );
};

export default MeetingJoinPage;

const FormPage = styled.div`
  height: 100vh;
  width: 100%;
`;

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  padding: 16px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const Message = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  line-height: 20px;
`;
